/**
 * A leitura na cota e o mapa entre o bruto e o resultado (onda 313, R9; spec 07 §6.1, item 4) — contas
 * puras, sem DOM.
 *
 * O `Resultado` do motor tem `n` = leituras do bruto − removidas (o corte do pré-furo, os picos, a
 * profundidade inválida), e as colunas qc, fs e u₂ dele são as da edição. A tabela anda no índice do
 * BRUTO (a leitura removida também tem linha, com o porquê), o cursor e a seleção andam na profundidade,
 * e a leitura na cota recebe o índice do bruto e acha a linha do resultado — e a do «antes», que pode ter
 * outra edição — cada uma pelo seu mapa (`removidas[].ordem` é o índice no bruto). Assim o depois de um
 * corte não lê a linha errada do antes: a 227ª leitura do Pagani do CPTu05 (2,27 m) é a 1ª do resultado
 * com o corte de 0 a 2,26 m e a 227ª sem ele.
 *
 * Em «Medidos» vão o qc, o fs e o u₂ como foram lidos (o bruto), e o editado ao lado quando difere. A
 * aplicabilidade das correlações segue a zona SBTn, e só ela (o motor, `correlacoes.ts`): o que é só das
 * argilosas diz «não se aplica (SBTn 7: arenosa)» numa areia, e vice-versa.
 */
import {
  GRANDEZAS, ZONAS_SBTN, ZONAS_SBTN_MOD, ZONAS_SCHNEIDER, zonaArenosa, zonaArgilosa,
} from '../motor';
import type { ColunaDoMotor, Grandeza, Leituras, Resultado } from '../motor';
import { SEM_VALOR, cientificaBr, numeroBr } from './escalas';
import { marcaDaColuna } from './homologacao';
import type { MarcaDeHomologacao } from './homologacao';

/**
 * O índice da leitura mais próxima de `alvo` (m), por busca binária numa profundidade crescente; no
 * empate, a de cima. Acima da primeira, a primeira; abaixo da última, a última; sem leitura ou sem alvo, −1.
 */
export function indiceNaProfundidade(z: ArrayLike<number>, alvo: number): number {
  const n = z.length;
  if (n === 0 || !Number.isFinite(alvo)) return -1;
  let lo = 0;
  let hi = n - 1;
  if (alvo <= z[0]) return 0;
  if (alvo >= z[hi]) return hi;
  while (hi - lo > 1) {
    const meio = (lo + hi) >> 1;
    if (z[meio] <= alvo) lo = meio;
    else hi = meio;
  }
  return alvo - z[lo] <= z[hi] - alvo ? lo : hi;
}

/**
 * O mapa entre o índice do bruto e o do resultado: `doBruto[k]` é a linha do resultado da leitura k do
 * bruto (−1 se a edição a tirou), e `doResultado[i]`, a leitura do bruto da linha i. As removidas são as
 * de `r.removidas` (o índice no bruto). Se o bruto e o resultado não forem da mesma vertical (as mantidas
 * não somam `r.n`), as que sobram ficam −1: nada se lê na linha errada.
 */
export function mapaDeIndices(r: Resultado, nBruto: number): { doBruto: Int32Array; doResultado: Int32Array } {
  const n = Math.max(0, Math.trunc(nBruto));
  const doBruto = new Int32Array(n).fill(-1);
  const doResultado = new Int32Array(Math.max(0, r.n)).fill(-1);
  const removida = new Uint8Array(n);
  for (const x of r.removidas) if (x.ordem >= 0 && x.ordem < n) removida[x.ordem] = 1;
  let i = 0;
  for (let k = 0; k < n; k++) {
    if (removida[k]) continue;
    if (i >= r.n) break;
    doBruto[k] = i;
    doResultado[i] = k;
    i++;
  }
  return { doBruto, doResultado };
}

/** Um grupo da leitura na cota: o título e as colunas, na ordem. */
export interface GrupoDaLeitura {
  titulo: string;
  colunas: readonly ColunaDoMotor[];
}

/**
 * Os grupos da leitura na cota (e da tabela): Medidos · Corrigidos e normalizados · Classificação ·
 * Correlações — todas as colunas do motor, cada uma uma vez, menos a profundidade (que é o cabeçalho) e
 * a porosidade (`nao_confere`: fica fora até ser explicada).
 */
export const GRUPOS_DA_LEITURA: readonly GrupoDaLeitura[] = Object.freeze([
  { titulo: 'Medidos', colunas: Object.freeze(['qc', 'fs', 'u2'] as ColunaDoMotor[]) },
  {
    titulo: 'Corrigidos e normalizados',
    colunas: Object.freeze([
      'qt', 'rf', 'gama', 'sv', 'u0', 'svl', 'qt1', 'fr', 'bq', 'U2', 'n', 'cn', 'qtn', 'ic', 'icSbt', 'ib',
    ] as ColunaDoMotor[]),
  },
  { titulo: 'Classificação', colunas: Object.freeze(['sbtn', 'sbtnMod', 'sbt', 'schneider'] as ColunaDoMotor[]) },
  {
    titulo: 'Correlações',
    colunas: Object.freeze([
      'su', 'suRazao', 'nkt', 'st', 'ocr', 'kocr', 'k0', 'resistencia', 'suR', 'dr', 'phi', 'phiPico', 'psi', 'kc',
      'qtncs', 'm', 'es', 'g0', 'vs', 'k', 'ch', 'n60', 'n160', 'w', 'e', 'gamaD',
    ] as ColunaDoMotor[]),
  },
].map((g) => Object.freeze(g)));

/**
 * As colunas que o motor só calcula nas zonas SBTn argilosas (1, 2, 3, 4 e 9) — `correlacoes.ts`, escolha
 * 1 — e as que só calcula nas arenosas (5 a 8). A resistência ao cisalhamento vale nas duas (o Su nas
 * argilosas, σ'v·tan φ' nas arenosas) e não está em nenhuma.
 */
export const SO_ARGILOSA: readonly Grandeza[] = Object.freeze(['nkt', 'su', 'suRazao', 'kocr', 'ocr', 'st', 'k0'] as Grandeza[]);
export const SO_ARENOSA: readonly Grandeza[] = Object.freeze(['dr', 'phi', 'es', 'psi'] as Grandeza[]);

/** Uma linha da leitura na cota. */
export interface LinhaDaLeitura {
  coluna: ColunaDoMotor;
  rotulo: string;
  unidade: string;
  /** O valor escrito (em «Medidos», o lido no bruto); «—» quando não há número. */
  texto: string;
  /** Em «Medidos», o valor da edição quando difere do lido; null nos demais e quando não difere. */
  editado: string | null;
  /** O valor do «antes» (o gravado, numa simulação) quando difere; null sem antes ou quando não difere. */
  antes: string | null;
  marca: MarcaDeHomologacao;
  /** Por que não há número: «não se aplica (SBTn 7: arenosa)», «não se aplica»; null quando há. */
  naoSeAplica: string | null;
}

/** O rótulo e a unidade de cada classificação. */
const CLASSIFICACOES: Readonly<Record<'sbt' | 'sbtn' | 'sbtnMod' | 'schneider', { rotulo: string }>> = {
  sbt: { rotulo: 'SBT' },
  sbtn: { rotulo: 'SBTn' },
  sbtnMod: { rotulo: 'SBTn modificado' },
  schneider: { rotulo: 'Schneider' },
};

const ehClassificacao = (c: ColunaDoMotor): c is 'sbt' | 'sbtn' | 'sbtnMod' | 'schneider' =>
  c === 'sbt' || c === 'sbtn' || c === 'sbtnMod' || c === 'schneider';

/** A zona escrita: «7 · areia densa a pedregulhosa», «7 · SD · arenoso — dilatante», «5 · 3 · solos de transição». */
function textoDaZona(c: 'sbt' | 'sbtn' | 'sbtnMod' | 'schneider', zona: number): string {
  if (c === 'sbtnMod') {
    const z = (ZONAS_SBTN_MOD as Readonly<Record<number, { sigla: string; nome: string }>>)[zona];
    return z ? `${zona} · ${z.sigla} · ${z.nome}` : 'sem classe';
  }
  if (c === 'schneider') {
    const z = ZONAS_SCHNEIDER[zona];
    return z ? `${z.codigo} · ${z.nome}` : 'sem classe';
  }
  const z = (ZONAS_SBTN as Readonly<Record<number, { nome: string }>>)[zona];
  return z ? `${zona} · ${z.nome}` : 'sem classe';
}

/** O valor de uma grandeza escrito como a tabela e a planilha escrevem (k e cₕ em notação científica). */
export function textoDoValor(g: Grandeza, v: number): string {
  if (!Number.isFinite(v)) return SEM_VALOR;
  if (g === 'k' || g === 'ch') return cientificaBr(v, GRANDEZAS[g].casas + 1);
  return numeroBr(v, GRANDEZAS[g].casas);
}

/** O texto de uma coluna numa linha do resultado (zona escrita ou número). */
function textoNa(r: Resultado, c: ColunaDoMotor, i: number): string {
  if (ehClassificacao(c)) return textoDaZona(c, r[c][i]);
  return textoDoValor(c, r.col[c][i]);
}

/** Por que a coluna não tem número nesta linha: a zona SBTn, quando é ela que decide. */
function porQueNaoSeAplica(c: ColunaDoMotor, zona: number): string {
  const argilosa = SO_ARGILOSA.includes(c as Grandeza);
  const arenosa = SO_ARENOSA.includes(c as Grandeza);
  if (argilosa || arenosa) {
    if (zona === 0) return 'não se aplica (sem zona SBTn)';
    if (argilosa && zonaArenosa(zona)) return `não se aplica (SBTn ${zona}: arenosa)`;
    if (arenosa && zonaArgilosa(zona)) return `não se aplica (SBTn ${zona}: argilosa)`;
  }
  return 'não se aplica';
}

/**
 * Tudo o que a tela escreve numa profundidade: a leitura `ordem` do BRUTO, a zona SBTn (com a marca de
 * homologação dela, `marcaDaZona` — o selo da zona não sai limpo se as premissas tiram o SBTn) e os quatro grupos
 * (`GRUPOS_DA_LEITURA`), cada linha com a marca de homologação (as premissas são as do resultado) e o
 * «antes» quando ele difere. Leitura que a edição tirou: `removida` diz o porquê («removida: pré-furo») e
 * só «Medidos» vem, com o que foi lido; leitura que o antes não tem: `semAntes`. As linhas que a tela não
 * mostra (`marca.mostrar` falso) não vêm.
 */
export function leituraNaCota(x: { bruto: Leituras; r: Resultado; antes?: Resultado | null; ordem: number }): {
  z: number;
  removida: string | null;
  semAntes: boolean;
  zona: { numero: number; nome: string; token: string } | null;
  /** A marca de homologação da zona SBTn (`marcaDaColuna('sbtn', r.premissas)`): o selo da zona a leva. */
  marcaDaZona: MarcaDeHomologacao;
  grupos: { titulo: string; linhas: LinhaDaLeitura[] }[];
} {
  const { bruto, r, ordem } = x;
  const antes = x.antes ?? null;
  const nBruto = bruto.profundidade.length;
  const z = ordem >= 0 && ordem < nBruto ? bruto.profundidade[ordem] : NaN;
  const i = ordem >= 0 && ordem < nBruto ? mapaDeIndices(r, nBruto).doBruto[ordem] : -1;
  const ia = antes && ordem >= 0 && ordem < nBruto ? mapaDeIndices(antes, nBruto).doBruto[ordem] : -1;
  const removidaEm = r.removidas.find((m) => m.ordem === ordem);
  const removida = i < 0 ? `removida: ${removidaEm?.motivo ?? 'sem leitura no resultado'}` : null;
  const semAntes = antes !== null && ia < 0;

  const lido = (c: 'qc' | 'fs' | 'u2'): number => (ordem >= 0 && ordem < nBruto ? bruto[c][ordem] : NaN);
  const grupos: { titulo: string; linhas: LinhaDaLeitura[] }[] = [];
  for (const g of GRUPOS_DA_LEITURA) {
    if (removida && g.titulo !== 'Medidos') continue;
    const linhas: LinhaDaLeitura[] = [];
    for (const c of g.colunas) {
      const marca = marcaDaColuna(c, r.premissas);
      if (!marca.mostrar) continue;
      const medido = c === 'qc' || c === 'fs' || c === 'u2';
      const rotulo = ehClassificacao(c) ? CLASSIFICACOES[c].rotulo : GRANDEZAS[c].rotulo;
      const unidade = ehClassificacao(c) ? '' : GRANDEZAS[c].unidade;
      const agora = i >= 0 ? textoNa(r, c, i) : SEM_VALOR;
      const texto = medido ? textoDoValor(c, lido(c)) : agora;
      const editado = medido && i >= 0 && agora !== texto ? agora : null;
      const doAntes = antes && ia >= 0 ? textoNa(antes, c, ia) : null;
      const comparado = medido ? (editado ?? texto) : texto;
      const valor = !ehClassificacao(c) && i >= 0 ? r.col[c][i] : NaN;
      linhas.push({
        coluna: c,
        rotulo,
        unidade,
        texto,
        editado,
        antes: doAntes !== null && doAntes !== comparado ? doAntes : null,
        marca,
        naoSeAplica: !ehClassificacao(c) && !medido && i >= 0 && !Number.isFinite(valor) ? porQueNaoSeAplica(c, r.sbtn[i]) : null,
      });
    }
    grupos.push({ titulo: g.titulo, linhas });
  }

  const numero = i >= 0 ? r.sbtn[i] : 0;
  const descricao = (ZONAS_SBTN as Readonly<Record<number, { nome: string; token: string }>>)[numero];
  return {
    z,
    removida,
    semAntes,
    zona: descricao ? { numero, nome: descricao.nome, token: descricao.token } : null,
    marcaDaZona: marcaDaColuna('sbtn', r.premissas),
    grupos,
  };
}
